import type { Project } from "./Project";

export interface HistoryEntry {
  label: string;
  before: Project;
  after: Project;
}

type Listener = (project: Project) => void;

export class History {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private listeners = new Set<Listener>();
  private current: Project;
  private maxSize = 100;

  constructor(initial: Project) {
    this.current = initial;
  }

  get state(): Project {
    return this.current;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn(this.current);
  }

  commit(label: string, after: Project) {
    const entry: HistoryEntry = {
      label,
      before: this.current,
      after: { ...after, modified: Date.now() },
    };
    this.undoStack.push(entry);
    if (this.undoStack.length > this.maxSize) this.undoStack.shift();
    this.redoStack = [];
    this.current = entry.after;
    this.emit();
  }

  replace(p: Project) {
    this.current = p;
    this.emit();
  }

  undo(): boolean {
    const entry = this.undoStack.pop();
    if (!entry) return false;
    this.redoStack.push(entry);
    this.current = entry.before;
    this.emit();
    return true;
  }

  redo(): boolean {
    const entry = this.redoStack.pop();
    if (!entry) return false;
    this.undoStack.push(entry);
    this.current = entry.after;
    this.emit();
    return true;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}
